/**
 * API Helpers
 *
 * Shared fetch utilities for client-side API hooks.
 * Extracted from useCanvas.ts to be reused across all data-fetching hooks.
 */

/**
 * Extended Error that preserves the API error code and HTTP status.
 * Thrown by apiCall when the response contains a `code` field.
 */
export class ApiError extends Error {
  readonly code: string | undefined
  readonly status: number

  constructor(message: string, status: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Type guard to check if an error is an ApiError with a specific code.
 */
export function isApiError(err: unknown, code?: string): err is ApiError {
  if (!(err instanceof ApiError)) return false
  if (code !== undefined) return err.code === code
  return true
}

/**
 * Make an authenticated API call.
 *
 * @param endpoint  - Path relative to VITE_API_BASE_URL (e.g. '/api/canvas')
 * @param isAuthenticated - Whether the current session is authenticated
 * @param options   - Standard fetch RequestInit options
 * @returns Parsed JSON response
 * @throws Error if not authenticated, base URL is missing, or response is not ok
 */
export async function apiCall(
  endpoint: string,
  isAuthenticated: boolean,
  options: RequestInit = {}
): Promise<unknown> {
  if (!isAuthenticated) {
    throw new Error('Not authenticated');
  }

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!apiBaseUrl) {
    throw new Error('VITE_API_BASE_URL is not configured');
  }

  const headers: Record<string, string> = { ...options.headers as Record<string, string> };
  if (options.body) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }

  const response = await fetch(`${apiBaseUrl}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as { error?: string; code?: string };
    // Only warn for expected client errors (auth, not-found); error for server failures
    if (response.status >= 500) {
      console.error(`API Error ${response.status}:`, errorData);
    }
    throw new ApiError(
      errorData.error || `HTTP ${response.status}`,
      response.status,
      errorData.code,
    );
  }

  return response.json();
}

/**
 * Call the AI Gateway service (VITE_AI_GATEWAY_URL).
 *
 * @param path    - Path relative to gateway URL (e.g. '/models/brands/image')
 * @param options - Standard fetch RequestInit options
 * @returns Parsed JSON response
 * @throws Error if gateway URL is not configured or response is not ok
 */
export async function aiGatewayCall(
  path: string,
  options: RequestInit = {}
): Promise<unknown> {
  const apiUrl = import.meta.env.VITE_AI_GATEWAY_URL;
  if (!apiUrl) {
    throw new Error('VITE_AI_GATEWAY_URL is not configured');
  }

  const response = await fetch(`${apiUrl}${path}`, {
    ...options,
    credentials: 'include' as const,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI Gateway error: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return response.json();
}

/**
 * Build a URL query string from a flat key-value object.
 * Skips undefined and null values. Prepends '?' if any params exist.
 *
 * @example
 *   buildQueryString({ page: 1, limit: 20, projectId: undefined })
 *   // → '?page=1&limit=20'
 */
export function buildQueryString(
  params: Record<string, string | number | boolean | undefined | null>
): string {
  const urlParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      urlParams.append(key, String(value));
    }
  }

  return urlParams.toString() ? `?${urlParams.toString()}` : '';
}
